import { z } from 'zod';
import type { OltFamily } from './olt-family.js';

/**
 * Dual-stack IPv6 on the subscriber WAN — the record, the observed truth, and
 * the two pure judgements every writer and every page shares.
 *
 * ## Why the record is NULLABLE and null means "not managed"
 *
 * Roughly 130 live subscriber WANs are ALREADY dual-stack by firmware default
 * with a plain-DHCP record: 24 of the 27 F670L V7.1 read `X_ZTE-COM_IPMode=Both`
 * + `IPv6AutoModeEnable=true` + `IPv6PrefixDelegationType=DHCP`, and 105 Huawei
 * INTERNET connections read `X_HW_IPv6Enable=true` (NBI, 2026-09-12). A stored
 * default of "IPv4" enforced on the next link or resync would rewrite every one
 * of them — the single biggest risk this feature carries. So the stored intent
 * is nullable, null is "leave the CPE as it has it" (no IPv6 leaf written,
 * nothing compared, no finding raised), and IPv4-only is an EXPLICIT choice.
 *
 * ## Why the vendor words live in ONE place
 *
 * One product class carries two dialects: the F670L V9.0 (the register test
 * ONU RTEGC6A2955E) spells IPv6 as `X_ZTE-COM_IPMode` / `Dhcpv6IANAEnable` /
 * `SlaacEnable` / `Dhcpv6IAPDEnable` / `IPv6AcquireMode`, while the 27 F670L
 * V7.1 subscribers spell it `X_ZTE-COM_IPMode` / `IPv6AddressingType` /
 * `IPv6AutoModeEnable` / `IPv6PrefixDelegationType`. The dialect is decided PER
 * CONNECTION by which leaves it reports at write time — never by product class,
 * because a recipe proven on one firmware says nothing about the other's keys.
 * `ipv6WritesFor` is the only module that knows a vendor word; `ipv6Refusals`
 * is the only module that knows which cells of the matrix have been observed.
 * Everything else — the API gate, the drain, the OMCI renderer, the compat
 * layer, the review step of the dialog — calls these two and quotes them.
 *
 * ## The evidence line
 *
 * OBSERVED = read off a live unit on the operator's ACS (NBI, 2026-09-12).
 * VERIFIED = written by this platform and read back. REFUSED = no observed
 * word anywhere; the sentence names what would verify the cell.
 *
 * VERIFIED at fleet scale on 2026-09-23, by the first `wan_ipv6` bulk run
 * (gachie1, 189 subscribers, dual stack with address None + DHCPv6-PD):
 *   huawei_xhw  X_HW_IPv6Enable=true, X_HW_IPv6.IPv6Address.1.Origin=None,
 *               X_HW_IPv6.IPv6Prefix.1.Origin=PrefixDelegation — read back off
 *               three units at their next inform.
 *   zte_v7      X_ZTE-COM_IPMode=Both, IPv6AddressingType=None,
 *               IPv6AutoModeEnable=false, IPv6PrefixDelegationType=DHCP — read
 *               back off ZXXPQN0R9E29403.
 * So "address None" and "prefix DHCPv6-PD" are now written-and-read words on
 * both dialects, not merely observed ones.
 */

export const ipv6AddressModeSchema = z.enum(['dhcpv6', 'auto', 'static', 'none']);
export type Ipv6AddressMode = z.infer<typeof ipv6AddressModeSchema>;

export const ipv6PrefixModeSchema = z.enum(['dhcpv6_pd', 'static', 'none']);
export type Ipv6PrefixMode = z.infer<typeof ipv6PrefixModeSchema>;

/**
 * The intent. A plain union, not a discriminatedUnion: zod 3 refuses a
 * `discriminatedUnion` whose member is a `ZodEffects` (the `.refine` below),
 * and the "not both none" rule is the one thing worth refining — a dual-stack
 * connection that neither takes an address nor requests a prefix is IPv4-only
 * wearing a different name.
 */
export const wanIpv6Schema = z.union([
  z.object({ protocol: z.literal('ipv4') }),
  z
    .object({
      protocol: z.literal('dual_stack'),
      addressMode: ipv6AddressModeSchema,
      prefixMode: ipv6PrefixModeSchema,
    })
    .refine((v) => !(v.addressMode === 'none' && v.prefixMode === 'none'), {
      message:
        'Dual stack with neither an IPv6 address mode nor a prefix mode is IPv4 only — choose one, or choose IPv4 only.',
    }),
]);
export type WanIpv6 = z.infer<typeof wanIpv6Schema>;

/** The stored form: absent or null = not managed. */
export const wanIpv6StoredSchema = wanIpv6Schema.nullable().default(null);

export const IPV6_ADDRESS_MODE_LABELS: Record<Ipv6AddressMode, string> = {
  dhcpv6: 'DHCPv6',
  auto: 'Auto',
  static: 'Static',
  none: 'None',
};
export const IPV6_PREFIX_MODE_LABELS: Record<Ipv6PrefixMode, string> = {
  dhcpv6_pd: 'DHCPv6-PD',
  static: 'Static',
  none: 'None',
};

/** The intent as a person would say it. */
export function describeWanIpv6(intent: WanIpv6 | null | undefined): string {
  if (!intent) return 'IPv6 not managed';
  if (intent.protocol === 'ipv4') return 'IPv4 only';
  return `dual stack (IPv6 address ${IPV6_ADDRESS_MODE_LABELS[intent.addressMode]}, prefix ${IPV6_PREFIX_MODE_LABELS[intent.prefixMode]})`;
}

/**
 * Which vocabulary a connection speaks, decided by the leaves it reports.
 *
 *   zte_v9      F670L V9.0 — `X_ZTE-COM_Dhcpv6IANAEnable` / `SlaacEnable` /
 *               `Dhcpv6IAPDEnable` / `IPv6AcquireMode` (the register test ONU only)
 *   zte_v7      F670L V7.1 — `X_ZTE-COM_IPv6AutoModeEnable` / `IPv6AddressingType`
 *               / `IPv6PrefixDelegationType` (27 subscribers)
 *   huawei_xhw  HG8546M / HG8145V6 / V / X6 / C / EG8141A5 — `X_HW_IPv6Enable`,
 *               `X_HW_IPv6.IPv6Address.1.Origin`, `X_HW_IPv6.IPv6Prefix.1.Origin`
 *               (the address/prefix rows EXIST only while Enable is true)
 *   tdtc        Tenda HG3 — numeric `X_TDTC_IPMode` whose codes are unverified;
 *               read-only
 *   tdtc_text   Tenda HG10c (v2.0.13, read 2026-10-21 on TDTCBB3CF648) — `X_TDTC_IPMode`
 *               in WORDS (`IPv4` on its management connection, `IPv4/IPv6` on its
 *               PPPoE WAN: both observed, so the protocol switch is writable),
 *               beside `X_TDTC_IPv6AddressMode`, whose words were PROBED on the unit
 *               the same day (operator: "set the address mode … and read the words";
 *               one SetParameterValues per spelling, a refused one faults 9007 and
 *               changes nothing): `Slaac`, `Static` and `None` are taken; SLAAC,
 *               DHCPv6, Dhcpv6, AutoConfigured, Auto, AutoDetect, AutoDetectMode,
 *               Autoconf, Stateful, Stateless, StatefulDHCPv6, StatelessDHCPv6,
 *               "Stateful DHCPv6", "Stateless DHCPv6(SLAAC)", "Auto Detect Mode",
 *               Dynamic, DHCP, SLAAC/DHCPv6, RA and the empty string are refused —
 *               so the stateful-DHCPv6 and auto-detect words are still unread.
 *               `X_TDTC_IPv6DHCPEnable` (`Disable`, empty), `X_TDTC_IPv6RequestOptions`
 *               (`ReAddr`, `NONE` read; the word for a delegated prefix unread),
 *               `X_TDTC_IPv6RequestDNS` (`Enable`, `None`) and the read-only
 *               `IPv6ConnStatus` / `IPv6Address` / `IPv6GatewayAddress` /
 *               `IPv6PrefixDelegationAddress` / `IPv6DNSServers1,2`. An accepted
 *               address-mode write re-dials the PPPoE session (uptime reset, read).
 *   none        no IPv6 leaf on the connection (Nokia G-140W-C); read-only
 */
export const ipv6DialectSchema = z.enum(['huawei_xhw', 'zte_v7', 'zte_v9', 'tdtc', 'tdtc_text', 'none']);
export type Ipv6Dialect = z.infer<typeof ipv6DialectSchema>;

/** What the CPE reports on its scored subscriber WAN — the truth beside the record. */
export const observedWanIpv6Schema = z.object({
  dialect: ipv6DialectSchema,
  protocol: z.enum(['ipv4', 'dual_stack', 'unknown']),
  addressMode: z.enum(['dhcpv6', 'auto', 'static', 'none', 'unknown']),
  prefixMode: z.enum(['dhcpv6_pd', 'static', 'none', 'unknown']),
  /** The vendor's IPv6 connection status leaf (`Connected` / `Unconfigured` / …), read-only. */
  connStatus: z.string().nullable(),
  /** The WAN link-local address where the model reports one — proof the stack is up. */
  linkLocal: z.string().nullable(),
  /**
   * The DATA the stack holds, read-only, typed per dialect (operator,
   * 2026-09-14: "the wan card in config doesn't show ipv6 data, just settings
   * — add them just under the ipv4 data"). The operator has no IPv6 upstream
   * yet, so on every live unit these read empty today (`::/0`, `::`, `""` —
   * all "none", so null / []); the fields exist for the day it arrives, and a
   * leaf the model does not carry is null too, never a guess.
   *
   *   address  the WAN's global address, with the prefix length as the CPE
   *            spells it (ZTE `X_ZTE-COM_IPv6ExternalAddress` "…/64" on the
   *            V7.1, `X_ZTE-COM_ExternalIPv6Address` on the V9; Huawei
   *            `X_HW_IPv6.IPv6Address.1.IPAddress` with `AddrMaskLen`)
   *   prefix   the delegated prefix (`X_ZTE-COM_IPv6PrefixDelegationAddress`,
   *            `X_ZTE-COM_PD`, `X_HW_IPv6.IPv6Prefix.1.Prefix`)
   *   gateway  `X_ZTE-COM_IPv6GatewayAddress`, `X_HW_IPv6.IPv6Address.1.DefaultGateway`
   *   dns      the V7.1's three `X_ZTE-COM_IPv6DNSServers1/2/3`, the V9's one
   *            comma-separated `X_ZTE-COM_IPv6DNSServers`; the HG8546M reads
   *            no IPv6 DNS leaf, so it is [] there
   */
  address: z.string().nullable(),
  prefix: z.string().nullable(),
  gateway: z.string().nullable(),
  dns: z.array(z.string()),
  /** Every IPv6 leaf read off the connection, by leaf name, so a page can show the raw words. */
  raw: z.record(z.string(), z.string()),
});
export type ObservedWanIpv6 = z.infer<typeof observedWanIpv6Schema>;

/**
 * Where an IPv6 intent would land, resolved by the API from the ONU row:
 *
 *   bridge  the internet row is bridged — the house router does IPv6
 *   omci    the ONU is OMCI-managed — the chassis would have to write it
 *   tr069   the ONU is TR-069-managed — its linked CPE's dialect, or `unknown`
 *           when no device has linked yet (the intent is stored and delivered
 *           by the pending drain on first link)
 */
export type Ipv6Target =
  | { kind: 'bridge' }
  | { kind: 'omci'; family: OltFamily; vendor: string | null }
  | { kind: 'tr069'; dialect: Ipv6Dialect | 'unknown'; serial: string | null };

/**
 * The register test ONU — the one unit a cross-dialect word may be tried on,
 * for the live proof, with a warning (memory: simpleacs-test-onu-register).
 */
export const IPV6_PROOF_SERIAL = 'RTEGC6A2955E';

/**
 * Sentences, per cell of the matrix, each saying exactly what would verify it.
 * Kept as constants so the docs, the tests and the pages quote one string.
 */
export const IPV6_REFUSAL = {
  bridge:
    'A bridged VLAN has no ONU-side addressing — the subscriber’s own router does IPv6. Set the IPv6 mode on a routed WAN, or leave it unmanaged.',
  omciC3xx:
    'IPv6 on the subscriber WAN is not written over OMCI on a C300. The `wan-ip <n> ipv6 mode dhcp|pppoe|static …` grammar was captured from `?` help on 2026-09-12 and has never been applied, and the ZXGPON MIB suggests it re-types the WAN’s address family rather than adding a stack — so it is refused rather than guessed at. Enable TR-069 management on this ONU and set IPv6 on the CPE instead; a supervised first apply on the register test ONU RTEGC6A2955E (gpon-onu_1/3/15:7), read back with `show onu running config` and `show gpon remote-onu ipv6-host … host <h>`, is what would verify the OMCI form.',
  omciC6xx:
    'An IPv6 WAN is not written to a C650. No captured C650 IPv6 form exists — the C300 answers `wan-ip <n> ipv6 mode dhcp|pppoe|static`, but this generation says even DHCP differently (`wan-ip <n> ipv4 mode dhcp … host <n>`) and its `ipv6` sibling has never been seen; probe `wan-ip 1 ?` and `wan-ip 1 ipv6 ?` on that chassis’s register ONU (HWTC3538900A, gpon_onu-1/1/1:6, restore required) before shipping it. Enable TR-069 management on this ONU and set IPv6 on the CPE instead.',
  omciHuawei:
    'IPv6 on the subscriber WAN is a CPE (TR-069) write: a Huawei OLT has no OLT-side IPv6 WAN verb — `ont ipconfig` is IPv4-only and Huawei’s own guide sets the ONT’s IPv6 WAN on the ONT. Enable TR-069 management on this ONU first (SmartOLT refuses the same way: "TR069 and Management IP are not enabled on the ONU"). The `?` probes that would settle a chassis form, the day a Huawei OLT is reachable: `ont ipconfig <p> <o> ?`, `ont ipconfig <p> <o> ip-index 1 ?`, `ont ipv6config ?`, `ont wan-profile profile-id 0` then `?`, `service-port ?`, `display ont wan-info <f>/<s>/<p> <o>`.',
  omciUnknownFamily:
    'IPv6 on the subscriber WAN is refused on a chassis whose generation this platform cannot classify from its model string — no OMCI IPv6 form is verified on any family, and an unclassified chassis is sent no guess. Enable TR-069 management on this ONU and set IPv6 on the CPE instead.',
  omciForeignHgu: (vendor: string) =>
    `This ${vendor} ONT takes its subscriber WAN — and its IPv6 mode — over TR-069; the OLT’s wan-ip is inert on it (the one Huawei on gachie1 holding an OMCI wan-ip reads Current IP 0.0.0.0). It is not linked to the ACS yet: summon it, then set the mode from its WAN editor.`,
  cpeNoLeaf:
    'This CPE reports no IPv6 leaf on its subscriber WAN, so the IPv6 mode cannot be written to it. Whether the firmware hides IPv6 under an object the ACS never walked is unverified — a refreshObject on InternetGatewayDevice.WANDevice.1, or the vendor’s TR-069 parameter reference, would settle it.',
  cpeTdtc:
    'The Tenda HG3 codes its IP mode as a number (X_TDTC_IPMode) whose values this platform has never seen written — only 1 has been read, beside IPv6ConnStatus=Unconfigured; the IPv6 mode is refused rather than guessed at. Set it on the ONU’s own page once, then the code it reports can be read off the NBI and put in the matrix.',
  tdtcTextAddressMode:
    'The Tenda HG10c spells its IPv6 address mode in X_TDTC_IPv6AddressMode. Words it takes, proven on the unit on 2026-10-21: Slaac (Auto), Static, None. The word for stateful DHCPv6 is unread — DHCPv6, Dhcpv6, Stateful, StatefulDHCPv6, “Stateful DHCPv6”, Dhcp and more were all refused 9007 — so DHCPv6 is refused rather than guessed at, and Static needs an address this platform does not write yet. Choose Auto or None; or set Stateful DHCPv6 on the router’s own page (WAN > WAN > PON WAN > Address Mode) once, and the word it then reports is read off the NBI.',
  tdtcTextPrefixMode:
    'The Tenda HG10c asks for a delegated prefix through X_TDTC_IPv6RequestOptions, and the only words read off it are ReAddr and NONE — the word for “request the prefix” is unread, so DHCPv6-PD is refused rather than guessed at. Tenda’s HG-series guide (V1.2) calls it Request Options (“obtain the prefix as a DHCPv6 client”) under WAN > WAN > PON WAN: switch it on there once, and the word the unit then reports is read off the NBI. Until then choose None.',
  staticAddress: (dialect: Ipv6Dialect | 'unknown') =>
    dialect === 'zte_v9'
      ? 'The F670L V9 CPE has no verified word for a static IPv6 address mode (X_ZTE-COM_IPv6AcquireMode has only ever read Auto; the X_ZTE-COM_ExternalIPv6Address format is unverified) — refused rather than guessed at. Observed on it today: DHCPv6, Auto, None.'
      : dialect === 'zte_v7'
        ? 'The F670L V7.1 CPE has no verified word for a static IPv6 address (X_ZTE-COM_IPv6AddressingType / IPv6GatewayType have only ever read None) — refused rather than guessed at. Observed on it today: Auto, None.'
        : dialect === 'huawei_xhw'
          ? 'The Huawei CPE has no verified word for a static IPv6 address (X_HW_IPv6.IPv6Address.1.Origin has only ever read None and AutoConfigured; TR-181 spells it Static, unverified here) — refused rather than guessed at. Observed on it today: Auto, None.'
          : 'A static IPv6 address is not written to any CPE yet — no dialect has an observed word for it (ZTE V9 X_ZTE-COM_IPv6AcquireMode, ZTE V7.1 X_ZTE-COM_IPv6AddressingType and Huawei X_HW_IPv6.IPv6Address.1.Origin have only ever read their auto/none words). Choose DHCPv6, Auto or None.',
  staticPrefix: (dialect: Ipv6Dialect | 'unknown') =>
    dialect === 'zte_v9'
      ? 'The F670L V9 CPE has no verified word for a static delegated prefix (X_ZTE-COM_PD has never been written; its format is unverified) — refused rather than guessed at. Observed on it today: DHCPv6-PD, None.'
      : dialect === 'zte_v7'
        ? 'The F670L V7.1 CPE has no verified word for a static delegated prefix (X_ZTE-COM_IPv6PrefixDelegationType has only ever read DHCP and None) — refused rather than guessed at. Observed on it today: DHCPv6-PD, None.'
        : dialect === 'huawei_xhw'
          ? 'The Huawei CPE has no verified word for a static delegated prefix (X_HW_IPv6.IPv6Prefix.1.Origin has only ever read PrefixDelegation) — refused rather than guessed at. Observed on it today: DHCPv6-PD.'
          : 'A static IPv6 prefix is not written to any CPE yet — no dialect has an observed word for it (ZTE V9 X_ZTE-COM_PD, ZTE V7.1 X_ZTE-COM_IPv6PrefixDelegationType and Huawei X_HW_IPv6.IPv6Prefix.1.Origin have only ever read their DHCPv6-PD/none words). Choose DHCPv6-PD or None.',
  dhcpv6OnZteV7:
    'The F670L V7.1 CPE has no observed word for DHCPv6 (IA_NA) on X_ZTE-COM_IPv6AddressingType — only None has ever been read, beside IPv6AutoModeEnable — so it is refused rather than guessed at. Observed on it today: Auto (X_ZTE-COM_IPv6AutoModeEnable=true), None. A supervised write on the register V7.1 unit ZXICCD591A29 (gpon-onu_1/3/8:6, snapshot before, byte-identical restore after) is what would verify the word.',
  dhcpv6OnHuawei:
    'The Huawei CPE has no observed word for DHCPv6 (IA_NA) on X_HW_IPv6.IPv6Address.1.Origin — only None and AutoConfigured have ever been read (TR-181 spells it DHCPv6, unverified here) — so it is refused rather than guessed at. Observed on it today: Auto (AutoConfigured), None. A supervised write on a register Huawei unit (HWTC0F186A0C, HWTC9E20C7B2 or HWTC7960C2A7 on gachie1; snapshot before, restore after) is what would verify the word.',
  prefixNoneOnHuawei:
    'X_HW_IPv6.IPv6Prefix.1.Origin has only ever read PrefixDelegation on this fleet — the word for "no prefix" is unobserved and refused rather than guessed at. Choose DHCPv6-PD, or IPv4 only, or leave IPv6 unmanaged.',
} as const;

/**
 * VERIFIED 2026-09-12 on the register test ONU RTEGC6A2955E (F670L V9.0.10P1N12A,
 * gpon-onu_1/3/15:7 on gachie1): three writes over TR-069, each read back off the
 * CPE at its next inform with no cwmp fault and the IPv4 WAN (10.2.0.7) untouched —
 * dual stack DHCPv6 + DHCPv6-PD (IPMode=Both, Dhcpv6IANAEnable=true,
 * SlaacEnable=false, Dhcpv6IAPDEnable=true), dual stack Auto + DHCPv6-PD
 * (SlaacEnable=true, Dhcpv6IANAEnable=false), and explicit IPv4
 * (IPMode=IPv4, all three false — the unit's original state). 'Both' IS the V9
 * word, so the cell is open to every V9 unit and the proof-only gate that
 * used to hold it (`dualStackOnZteV9Unproven`, `IPV6_ZTE_V9_BOTH_WARNING`) is
 * gone. The serial is kept as the record of where the proof ran.
 */

/**
 * Why an intent cannot land on its target — empty when it can.
 *
 * Pure, so the API gate, the pending drain, the OMCI renderer, the compat
 * layer and the dialog's review step all read one rule. A null intent is
 * "not managed" and is never refused: it writes nothing.
 */
export function ipv6Refusals(intent: WanIpv6 | null | undefined, target: Ipv6Target): string[] {
  if (!intent) return [];
  const issues: string[] = [];

  if (target.kind === 'bridge') return [IPV6_REFUSAL.bridge];

  if (target.kind === 'omci') {
    // A non-ZTE HGU on OMCI ignores the chassis's wan-ip entirely; that answer
    // comes before the family's, because it is the one that names the way out.
    const vendor = (target.vendor ?? '').toLowerCase();
    if (vendor && vendor !== 'zte') return [IPV6_REFUSAL.omciForeignHgu(target.vendor!)];
    switch (target.family) {
      case 'c3xx':
        return [IPV6_REFUSAL.omciC3xx];
      case 'c6xx':
        return [IPV6_REFUSAL.omciC6xx];
      case 'ma56xx':
      case 'ma58xx':
        return [IPV6_REFUSAL.omciHuawei];
      default:
        return [IPV6_REFUSAL.omciUnknownFamily];
    }
  }

  // TR-069.
  const { dialect } = target;
  if (dialect === 'none') return [IPV6_REFUSAL.cpeNoLeaf];
  if (dialect === 'tdtc') return [IPV6_REFUSAL.cpeTdtc];
  if (dialect === 'tdtc_text') {
    // The HG10c's two IPMode words are both observed; Slaac / Static / None
    // were proven on NetworkCentre SHOP (2026-10-21); stateful DHCPv6 and the
    // prefix-request word are not read, and a static address is not written.
    if (intent.protocol === 'ipv4') return [];
    if (intent.addressMode !== 'none' && intent.addressMode !== 'auto') issues.push(IPV6_REFUSAL.tdtcTextAddressMode);
    if (intent.prefixMode !== 'none') issues.push(IPV6_REFUSAL.tdtcTextPrefixMode);
    return issues;
  }
  if (intent.protocol === 'ipv4') return [];

  if (intent.addressMode === 'static') issues.push(IPV6_REFUSAL.staticAddress(dialect));
  if (intent.prefixMode === 'static') issues.push(IPV6_REFUSAL.staticPrefix(dialect));
  if (intent.addressMode === 'dhcpv6' && dialect === 'zte_v7') issues.push(IPV6_REFUSAL.dhcpv6OnZteV7);
  if (intent.addressMode === 'dhcpv6' && dialect === 'huawei_xhw') issues.push(IPV6_REFUSAL.dhcpv6OnHuawei);
  if (intent.prefixMode === 'none' && dialect === 'huawei_xhw') issues.push(IPV6_REFUSAL.prefixNoneOnHuawei);
  return issues;
}

/**
 * One ordered step of the CPE write. `paths` is a setParameterValues; a step
 * carrying `refreshObject` (and no paths) asks the ACS to re-read that object
 * before the next step — Huawei's `X_HW_IPv6.IPv6Address.1` / `IPv6Prefix.1`
 * rows normally appear once `X_HW_IPv6Enable` is true, so the document has to
 * be re-read before a row is addressed; where the CPE reports a table EMPTY
 * even then (HWTC464B019D and ten more, 2026-09-25) the writer creates the row
 * (`ensureIpv6Row` in the API's wan.ts) and writes on the instance it gets. The object name carries NO trailing dot:
 * GenieACS builds the provision as `${objectName}.[…]` and rejects the double
 * dot as an invalid path.
 */
export interface Ipv6WriteBatch {
  paths: Array<[string, string | boolean]>;
  refreshObject?: string;
  note?: string;
}

/**
 * The exact parameter writes a dialect takes for an intent, in order — the
 * ONLY place a vendor word lives. Throws on a cell that has no word, so a
 * refused combination can never be rendered as an empty write; run
 * `ipv6Refusals` first.
 */
export function ipv6WritesFor(
  dialect: Ipv6Dialect,
  intent: WanIpv6,
  connectionPath: string,
  serial: string | null,
): Ipv6WriteBatch[] {
  const P = connectionPath.replace(/\.$/, '');
  const leaf = (name: string) => `${P}.${name}`;

  if (dialect === 'zte_v9') {
    if (intent.protocol === 'ipv4') {
      // Every value observed on the unit itself (IPMode=IPv4, the three booleans false).
      return [
        {
          paths: [
            [leaf('X_ZTE-COM_IPMode'), 'IPv4'],
            [leaf('X_ZTE-COM_Dhcpv6IANAEnable'), false],
            [leaf('X_ZTE-COM_SlaacEnable'), false],
            [leaf('X_ZTE-COM_Dhcpv6IAPDEnable'), false],
          ],
        },
      ];
    }
    assertNoStatic(dialect, intent);
    return [
      {
        paths: [
          [leaf('X_ZTE-COM_IPMode'), 'Both'],
          [leaf('X_ZTE-COM_Dhcpv6IANAEnable'), intent.addressMode === 'dhcpv6'],
          [leaf('X_ZTE-COM_SlaacEnable'), intent.addressMode === 'auto'],
          [leaf('X_ZTE-COM_Dhcpv6IAPDEnable'), intent.prefixMode === 'dhcpv6_pd'],
        ],
        // X_ZTE-COM_IPv6AcquireMode is left as read: 'Auto' is the only value ever seen.
      },
    ];
  }

  if (dialect === 'zte_v7') {
    if (intent.protocol === 'ipv4') {
      // Observed on the 4 IPv4-only subscriber connections and all 27 management ones.
      return [
        {
          paths: [
            [leaf('X_ZTE-COM_IPMode'), 'IPv4'],
            [leaf('X_ZTE-COM_IPv6AutoModeEnable'), false],
            [leaf('X_ZTE-COM_IPv6PrefixDelegationType'), 'None'],
          ],
        },
      ];
    }
    assertNoStatic(dialect, intent);
    if (intent.addressMode === 'dhcpv6') throw new Error(IPV6_REFUSAL.dhcpv6OnZteV7);
    const auto = intent.addressMode === 'auto';
    const pd = intent.prefixMode === 'dhcpv6_pd';
    // (Both, None, true, DHCP) is the exact live shape on 24 units; the other
    // two tuples are composed from observed parts and say so.
    const composed = !(auto && pd);
    return [
      {
        paths: [
          [leaf('X_ZTE-COM_IPMode'), 'Both'],
          [leaf('X_ZTE-COM_IPv6AddressingType'), 'None'],
          [leaf('X_ZTE-COM_IPv6AutoModeEnable'), auto],
          [leaf('X_ZTE-COM_IPv6PrefixDelegationType'), pd ? 'DHCP' : 'None'],
        ],
        ...(composed
          ? {
              note: `The tuple (IPMode=Both, AddressingType=None, AutoModeEnable=${auto}, PrefixDelegationType=${pd ? 'DHCP' : 'None'}) is composed from words observed separately on F670L V7.1 units; the live fleet shape is (Both, None, true, DHCP).`,
            }
          : {}),
      },
    ];
  }

  if (dialect === 'huawei_xhw') {
    if (intent.protocol === 'ipv4') {
      // Observed false on 238 connections.
      return [{ paths: [[leaf('X_HW_IPv6Enable'), false]] }];
    }
    assertNoStatic(dialect, intent);
    if (intent.addressMode === 'dhcpv6') throw new Error(IPV6_REFUSAL.dhcpv6OnHuawei);
    if (intent.prefixMode === 'none') throw new Error(IPV6_REFUSAL.prefixNoneOnHuawei);
    return [
      // ONE leaf per set — the X_HW_LANBIND cwmp.9002 precedent: the HG8546M
      // faults a bulk set and takes the leaves singly. Whether it takes the
      // X_HW_IPv6.* leaves in one set is unverified, so one per set until proven.
      { paths: [[leaf('X_HW_IPv6Enable'), true]] },
      {
        paths: [],
        refreshObject: leaf('X_HW_IPv6'),
        note: 'The IPv6Address / IPv6Prefix rows appear once X_HW_IPv6Enable is true; the object is re-read before a row is addressed, and a row the CPE reports an empty table for is created first.',
      },
      {
        paths: [
          [
            leaf('X_HW_IPv6.IPv6Address.1.Origin'),
            intent.addressMode === 'auto' ? 'AutoConfigured' : 'None',
          ],
        ],
      },
      { paths: [[leaf('X_HW_IPv6.IPv6Prefix.1.Origin'), 'PrefixDelegation']] },
    ];
  }

  if (dialect === 'tdtc_text') {
    // Every word below was READ off one HG10c (TDTCBB3CF648, 2026-10-21): IPv4 on
    // its management connection, IPv4/IPv6 on its PPPoE WAN; Slaac, Static and
    // None taken by X_TDTC_IPv6AddressMode when probed (every other spelling
    // faulted 9007); NONE read on X_TDTC_IPv6RequestOptions. No other word is
    // spelled here. An accepted address-mode write re-dials the PPPoE session.
    if (intent.protocol === 'ipv4') return [{ paths: [[leaf('X_TDTC_IPMode'), 'IPv4']] }];
    if (intent.addressMode !== 'none' && intent.addressMode !== 'auto') throw new Error(IPV6_REFUSAL.tdtcTextAddressMode);
    if (intent.prefixMode !== 'none') throw new Error(IPV6_REFUSAL.tdtcTextPrefixMode);
    return [
      {
        paths: [
          [leaf('X_TDTC_IPMode'), 'IPv4/IPv6'],
          [leaf('X_TDTC_IPv6AddressMode'), intent.addressMode === 'auto' ? 'Slaac' : 'None'],
          [leaf('X_TDTC_IPv6RequestOptions'), 'NONE'],
        ],
        note: 'The HG10c re-dials its PPPoE session when its IPv6 address mode changes (read 2026-10-21: uptime reset after an accepted write).',
      },
    ];
  }
  if (dialect === 'tdtc') throw new Error(IPV6_REFUSAL.cpeTdtc);
  void serial;
  throw new Error(IPV6_REFUSAL.cpeNoLeaf);
}

function assertNoStatic(dialect: Ipv6Dialect, intent: WanIpv6): void {
  if (intent.protocol !== 'dual_stack') return;
  if (intent.addressMode === 'static') throw new Error(IPV6_REFUSAL.staticAddress(dialect));
  if (intent.prefixMode === 'static') throw new Error(IPV6_REFUSAL.staticPrefix(dialect));
}

/**
 * Read the three stored columns back into an intent. Null when the row
 * records nothing (the ~130 live dual-stack-by-default units), and null too
 * when the columns disagree with the contract — a half-written row is not a
 * mode this platform would ever enforce.
 */
export function wanIpv6FromColumns(row: {
  ipProtocol: string | null;
  ipv6AddressMode: string | null;
  ipv6PrefixMode: string | null;
}): WanIpv6 | null {
  if (row.ipProtocol === 'ipv4') return { protocol: 'ipv4' };
  if (row.ipProtocol !== 'dual_stack') return null;
  const parsed = wanIpv6Schema.safeParse({
    protocol: 'dual_stack',
    addressMode: row.ipv6AddressMode,
    prefixMode: row.ipv6PrefixMode,
  });
  return parsed.success ? parsed.data : null;
}

/** The three columns an intent stores as — all null for "not managed". */
export function wanIpv6ToColumns(intent: WanIpv6 | null): {
  ipProtocol: string | null;
  ipv6AddressMode: string | null;
  ipv6PrefixMode: string | null;
} {
  if (!intent) return { ipProtocol: null, ipv6AddressMode: null, ipv6PrefixMode: null };
  if (intent.protocol === 'ipv4') return { ipProtocol: 'ipv4', ipv6AddressMode: null, ipv6PrefixMode: null };
  return {
    ipProtocol: 'dual_stack',
    ipv6AddressMode: intent.addressMode,
    ipv6PrefixMode: intent.prefixMode,
  };
}
